import { TCommonData, TWidget, TWidgetCommonOption } from '@libs/types';
import {
    CoreButtonView,
    CoreLayoutView,
    CoreTextView,
    useThemeHook,
    type TCoreButtonStyleProps,
    type TCoreButtonViewProps,
    type TCoreLayoutOptions,
    type TCoreLayoutStyleProps,
} from '@core/widgets';
import React, { MouseEventHandler } from 'react';
import { TButtonOption } from '../../types/options';
import styles from './styles/buttonBanner.module.css';

export type TButtonBannerViewProps = {
    uid?: string;
    label?: string;
    // red point in the corner
    hasNotification?: boolean;
    margin?: TButtonOption['margin'];
    onClick: MouseEventHandler<HTMLButtonElement>;
    widget?: TCoreButtonViewProps['widget'];
    parentStyleNames?: string[];
} & Pick<TWidgetCommonOption, 'isDisabled' | 'isHide'>;

export const ButtonBannerView = ({
    uid,
    label,
    hasNotification = true,
    margin,
    isDisabled,
    isHide,
    onClick,
    widget,
    parentStyleNames: parentStyleNamesProp,
}: TButtonBannerViewProps) => {
    const parentStyleNames = parentStyleNamesProp ?? [];

    const badgeWidget = {
        uid: '',
        data: {} as TCommonData,
        options: { style: 'buttonBannerBadge', parentStyleNames },
    } as TWidget<TCommonData, TCoreLayoutOptions>;
    const { palette: badgePalette } = useThemeHook(badgeWidget);

    const themedSize = (badgePalette as Record<string, unknown>).size as number | undefined;
    const themedMain = (badgePalette as Record<string, unknown>).main as string | undefined;
    const themedStroke = (badgePalette as Record<string, unknown>).borderStroke as number | undefined;
    const innerSize = themedSize ?? 16;
    const stroke = themedStroke ?? 1.5;
    const totalSize = innerSize + stroke;

    const styleProps: TCoreButtonStyleProps = {
        main: 'var(--color-white)',
        onMain: 'var(--color-black)',
        hovered: {
            main: 'var(--color-grey_16_lc)',
            onMain: 'var(--color-black)',
        },
        pressed: {
            main: 'var(--color-grey_8_lc)',
            onMain: 'var(--color-black)',
        },
        disabled: {
            main: 'var(--color-white)',
            onMain: 'var(--color-black)',
        },
        padding: { top: 8, right: 16, bottom: 8, left: 16 },
        borderRadius: 20,
        fontStyle: { fontSize: 17, lineHeight: 24, fontWeight: 500 },
        fillContainer: true,
        cursorDisabled: 'default',
    };

    const coreTextStyleProps = { main: 'var(--color-black)' };

    const badgeStyleProps: TCoreLayoutStyleProps = {
        width: totalSize,
        height: totalSize,
        borderRadius: totalSize,
        backgroundColor: themedMain ?? 'var(--icon-error, #FF4778)',
        borderStroke: stroke,
        borderColor: 'var(--border-on-fill-secondary, #F3F2F5)',
    };

    const resolvedWidget: TCoreButtonViewProps['widget'] = widget
        ? ({ ...widget, options: { ...widget.options, margin: undefined } } as TCoreButtonViewProps['widget'])
        : ({ uid: uid ?? '', data: { text: label }, options: {} } as TCoreButtonViewProps['widget']);

    const wrapperStyle: React.CSSProperties = {
        ...(isHide ? { display: 'none' } : null),
        ...(margin?.top !== undefined ? { marginTop: `${margin.top}px` } : null),
        ...(margin?.right !== undefined ? { marginRight: `${margin.right}px` } : null),
        ...(margin?.bottom !== undefined ? { marginBottom: `${margin.bottom}px` } : null),
        ...(margin?.left !== undefined ? { marginLeft: `${margin.left}px` } : null),
    };

    return (
        <div className={styles.buttonBannerWrapper} style={wrapperStyle}>
            <CoreButtonView
                widget={resolvedWidget}
                text={label}
                isDisabled={isDisabled}
                isHide={isHide}
                styleProps={styleProps}
                parentStyleNames={parentStyleNames}
                styleName="buttonBanner"
                onClick={onClick}
            >
                <CoreTextView text={label} as="span" parentStyleNames={parentStyleNames} styleName="buttonBannerLabel" styleProps={coreTextStyleProps} />
            </CoreButtonView>
            {hasNotification && (
                <div className={styles.buttonBannerBadge} aria-hidden="true">
                    <CoreLayoutView styleProps={badgeStyleProps} styleName="buttonBannerBadge" parentStyleNames={parentStyleNames} />
                </div>
            )}
        </div>
    );
};
